import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, EmptyState, LoadingState, kstDotDateStr } from '@biznomi/ui-react'
import {
  fileSizeText,
  getMyTrainings,
  openTrainingFile,
  type MyTraining,
} from '@/lib/api/training'

/**
 * 내 교육 증빙 (직원).
 *
 * 급여명세서 화면과 **다른 점 하나** — 배지가 없다.
 * 거기는 「새 명세서 / 확인함」 이 있는데, 그건 교부가 법적 의무라 열람이 증빙이기
 * 때문이다. 교육은 교부 의무가 없어 증빙은 회사가 올린 파일 그 자체다. 그래서
 * `viewedAt` 칸을 아예 안 만들었고, 안 만든 것을 화면에서 흉내 내지 않는다.
 * (배지를 그리면 "안 봤다" 가 상태가 되고, 그건 미이수 알림과 한 칸 차이다)
 */
export default function Trainings() {
  const [opening, setOpening] = useState<string | null>(null)
  const [error, setError] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['trainings', 'mine'],
    queryFn: getMyTrainings,
  })
  const rows = data?.success ? data.data ?? [] : []

  async function view(t: MyTraining) {
    setOpening(t.id)
    setError('')
    const res = await openTrainingFile(t.id)
    setOpening(null)

    if ('error' in res) {
      setError(res.error)
      return
    }

    // 새 탭에서 연다. iOS 는 iframe 안의 PDF 를 자주 못 그리는데 blob 탭은 뜬다.
    const win = window.open(res.url, '_blank')
    if (!win) window.location.href = res.url
    setTimeout(() => URL.revokeObjectURL(res.url), 60_000)
  }

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold">교육 증빙</h1>

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState
          title="아직 올라온 이수증이 없습니다"
          hint="회사에서 이수증을 올리면 여기에 뜹니다."
        />
      ) : (
        <ul className="space-y-3">
          {rows.map((t) => (
            <li key={t.id} className="space-y-3 rounded-card bg-white p-4">
              <div className="min-w-0">
                <p className="font-semibold">{t.title}</p>
                <p className="mt-0.5 text-sm text-slate-600">
                  이수일 {kstDotDateStr(t.completedOn)}
                </p>
                <p className="mt-0.5 truncate text-xs text-slate-400">
                  {t.fileName} · {fileSizeText(t.fileSize)}
                </p>
              </div>

              <Button
                variant="secondary"
                className="w-full"
                loading={opening === t.id}
                onClick={() => void view(t)}
              >
                이수증 보기
              </Button>
            </li>
          ))}
        </ul>
      )}

      {rows.length > 0 && (
        /*
          이수 여부에 대해 우리가 하는 말은 없다. 기한도 주기도 안 본다 —
          그걸 보기 시작하면 자동 판정이 된다 (영구 폐기 항목).
        */
        <p className="text-xs leading-relaxed text-slate-400">
          회사에서 올린 이수증 그대로입니다. 올린 뒤에 바뀌지 않았는지 저희가 확인해서 보여
          드립니다. 어떤 교육을 언제까지 받아야 하는지는 회사 담당자에게 물어보세요.
        </p>
      )}
    </div>
  )
}
